import { ListBulletsIcon } from '@phosphor-icons/react';
import {
  Cell,
  Checkbox,
  Column,
  EmptyState,
  Pagination,
  Row,
  Table,
  TableBody,
  TableContainer,
  TableHeader,
} from '@skupreme/shared/ui';
import { SUGGESTIONS_PAGE_SIZE } from '../../lib/suggestion-params';
import type {
  MappingSuggestionRow,
  SuggestionView,
} from '../../lib/suggestion-types';
import type { RowHandlers } from './SuggestionActions';
import { SuggestionRow } from './SuggestionRow';

const LOADING_ROWS = 7;
const COLUMN_COUNT = 5;
const ONLY_PAGE_SIZE = [SUGGESTIONS_PAGE_SIZE];

export type SuggestionsTableProps = {
  view: SuggestionView;
  rows: MappingSuggestionRow[];
  loading: boolean;
  total: number;
  page: number;
  onPageChange: (page: number) => void;
  selectedIds: ReadonlySet<string>;
  onToggle: (row: MappingSuggestionRow) => void;
  onTogglePage: () => void;
  handlers: RowHandlers;
  empty: { title: string; description?: string };
  isReadOnly?: boolean;
  isActionsHidden?: boolean;
  isBusy?: boolean;
};

/** One queue of the Mapping tab: select-page header, 50 rows a page, server paging. */
export function SuggestionsTable({
  view,
  rows,
  loading,
  total,
  page,
  onPageChange,
  selectedIds,
  onToggle,
  onTogglePage,
  handlers,
  empty,
  isReadOnly,
  isActionsHidden,
  isBusy,
}: SuggestionsTableProps) {
  const onPage = rows.filter(row => selectedIds.has(row.listingId)).length;

  return (
    <TableContainer className="sk:overflow-visible sk:rounded-card sk:bg-surface">
      <Table aria-label="Listings to map" className="sk:table-fixed">
        <TableHeader>
          <Column id="select" className="sk:w-10">
            <Checkbox
              aria-label="Select this page"
              isDisabled={!rows.length}
              isSelected={!!rows.length && onPage === rows.length}
              isIndeterminate={onPage > 0 && onPage < rows.length}
              onChange={onTogglePage}
            />
          </Column>
          <Column id="listing" isRowHeader className="sk:w-[32%]">
            Listing
          </Column>
          <Column id="arrow" aria-label="Matches" className="sk:w-8">
            {''}
          </Column>
          <Column id="product">Product</Column>
          <Column id="actions" aria-label="Actions" className="sk:w-[17rem]">
            {''}
          </Column>
        </TableHeader>
        <TableBody
          renderEmptyState={() => (
            <EmptyState
              icon={<ListBulletsIcon size={20} />}
              title={empty.title}
              description={empty.description}
            />
          )}
        >
          {loading
            ? Array.from({ length: LOADING_ROWS }, (_, row) => (
                <Row key={`placeholder-${row}`} id={`placeholder-${row}`}>
                  <Cell colSpan={COLUMN_COUNT}>
                    <div className="sk:h-10 sk:animate-pulse sk:rounded-option sk:bg-fill-tertiary" />
                  </Cell>
                </Row>
              ))
            : rows.map(row => (
                <SuggestionRow
                  key={row.listingId}
                  view={view}
                  row={row}
                  isSelected={selectedIds.has(row.listingId)}
                  onToggle={onToggle}
                  handlers={handlers}
                  isReadOnly={isReadOnly}
                  isActionsHidden={isActionsHidden}
                  isBusy={isBusy}
                />
              ))}
        </TableBody>
      </Table>
      {total > 0 && (
        <Pagination
          page={page}
          pageSize={SUGGESTIONS_PAGE_SIZE}
          total={total}
          pageSizeOptions={ONLY_PAGE_SIZE}
          onPageChange={next => {
            onPageChange(next);
            window.scrollTo({ top: 0 });
          }}
          onPageSizeChange={() => {}}
        />
      )}
    </TableContainer>
  );
}
